export type RawCard = {
  id?: string;
  _id?: string;
  title?: string;
  name?: string;
  subtitle?: string;
  description?: string;
  cardType?: string; // 'physical' | 'magic' etc
  cardUrl?: string;
  imageUrl?: string;
  img?: string;
  image?: string;
  hpCost?: number | string;
  spCost?: number | string;
  keywords?: string[] | string;
  deckKind?: string;
  specialType?: string;
  acquisitionLocked?: boolean;
};

// CRA env var (set in .env as REACT_APP_CARDS_API). Fallback to public demo API.
// Use a safe runtime guard so we don't crash in the browser if `process` isn't defined.
const API =
  (typeof process !== 'undefined' && (process as any).env && (process as any).env.REACT_APP_CARDS_API)
    ? (process as any).env.REACT_APP_CARDS_API
    : 'https://card-backend-trwc.onrender.com/api/cards?folder=cards_batch2';

type CacheShape = { savedAt: number; cards: RawCard[] };

const BUILTIN_CARD_SPECS: RawCard[] = [
  {
    id: 'builtin-lower-the-burden',
    title: 'Lower the Burden',
    subtitle: 'Utility',
    cardType: 'magic',
    description: 'This turn, all <b>Utility</b> cards costs are reduced by 10.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-mend-the-wound',
    title: 'Mend the Wound',
    subtitle: 'Utility',
    cardType: 'magic',
    description: 'Choose one: <p>Gain 40 HP or Gain 40 SP.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-sneak-attack',
    title: 'Sneak Attack',
    subtitle: 'Attack',
    cardType: 'physical',
    description: 'Deal 15 damage. <p>Ignore enemy Armor.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-confidence',
    title: 'Confidence',
    subtitle: 'Utility',
    cardType: 'magic',
    description: 'Lose all your <b>Armor</b>. Gain the same amount of SP.<p>Lose all your <b>Reflect</b>. Gain the same amount of HP.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-overflow-burst',
    title: 'Overflow Burst',
    subtitle: 'Utility',
    cardType: 'magic',
    description: 'If you would gain <b>Reflect</b> above your cap, deal the excess as damage instead.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-forsaken-tome',
    title: 'Forsaken Tome',
    subtitle: 'Utility',
    cardType: 'magic',
    description: 'Look through your discard pile and choose a card. <p>Put that card in your hand.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-glass-reprisal',
    title: 'Glass Reprisal',
    subtitle: 'Attack',
    cardType: 'magic',
    description: 'Deal damage equal to your current <b>Reflect</b>.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-fate-reversed',
    title: 'Fate Reversed',
    subtitle: 'Utility',
    cardType: 'magic',
    description: 'During this turn, every discarded card is also played. <p><b>Exile</b>.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69add3c6e3b805d4a0daf6f2',
    title: 'Crushing Bite',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 30 damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69add37be3b805d4a0daf6ef',
    title: 'Acidic Silk',
    subtitle: 'Enemy Defense',
    cardType: 'physical',
    description: '<b>Reflect</b> 25% of the next incoming damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69add2a4e3b805d4a0daf6e9',
    title: 'Web Cocoon',
    subtitle: 'Enemy Defense',
    cardType: 'physical',
    description: 'Gain 35 Armor.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69c23cf781fa6b75f1a2b274',
    title: 'Self-punishment',
    subtitle: 'Enemy Utility',
    cardType: 'physical',
    description: 'Lose 5 HP.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '6998d68b0bebb02c4299bfe8',
    title: 'Iron Rend',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Destroy all enemy armor. <p>Deal 50 damage.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-monsignor-decree-of-restraint',
    title: 'Decree of Restraint',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Issue a random decree for the player\'s next turn. If they break it, add 1 Guilt to their discard pile.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-monsignor-crozier-strike',
    title: 'Crozier Strike',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 8 damage + 5 for each Guilt card in the player\'s hand at the beginning of this enemy turn.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-monsignor-brand-of-heresy',
    title: 'Brand of Heresy',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Add 1 Guilt to the player\'s discard pile.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-monsignor-penance-tax',
    title: 'Penance Tax',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Deal 1 damage for each Guilt card in the player\'s deck.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-monsignor-false-absolution',
    title: 'False Absolution',
    subtitle: 'Enemy Defense',
    cardType: 'magic',
    description: 'Gain 10 Armor + 5 for each Guilt card in the player\'s hand at the beginning of this enemy turn.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-monsignor-judgment',
    title: 'Judgment',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Only playable if the player\'s deck contains at least 13 Guilt cards. Deal 99 damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-monsignor-litany-of-shame',
    title: 'Litany of Shame',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Only playable if the player\'s deck contains at least 3 Guilt cards. The player draws 3 cards. For each Guilt drawn this way, they lose 5 SP.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-dark-apostle-profane-restoration',
    title: 'Profane Restoration',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Gain 40 HP.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-dark-apostle-soul-drain',
    title: 'Soul Drain',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Deal 20 damage. Gain 20 HP.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-dark-apostle-cursed-fate',
    title: 'Cursed Fate',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Whenever the enemy ends their turn without attacking, deal 20 damage and burn 20 SP. This effect persists until they attack.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-dark-apostle-punish',
    title: 'Punish',
    subtitle: 'Enemy Defense',
    cardType: 'magic',
    description: '<b>Reflect</b> 100% of the next damage you would take.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cabe41b67bdde39f741f45',
    title: 'Eviscerate',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 40 damage. This attack cannot trigger Reflect. <p>If the target is Marked, they lose 20 SP.</p>',
    hpCost: 0,
    spCost: 0,
  },

  {
    id: '69ccf311bbea17fa136d85d9',
    title: 'Caustic Flask',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Deal 16 damage. Burn 10 SP.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ccf390bbea17fa136d85db',
    title: 'Corrosive Mist',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Deal 8 damage.<p>Deal 18 damage. <b>Delay (1)</b>.</p><p>Deal 28 damage. <b>Delay (2)</b>.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ccf3fdbbea17fa136d85e0',
    title: 'Reinjection',
    subtitle: 'Enemy Defense',
    cardType: 'magic',
    description: 'Gain 20 Armor. Gain 20 HP.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ccf49cbbea17fa136d85e2',
    title: 'Volatile Reaction',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Deal 38 damage. Consume your Active Compound.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ccf50ebbea17fa136d85e4',
    title: 'Caustic Compound',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Active Compound. Start of player turn: deal 4 damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ccf53cbbea17fa136d85e6',
    title: 'Mindrot Compound',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Active Compound. Start of player turn: burn 6 SP.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ccf583bbea17fa136d85e8',
    title: 'Iron Compound',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Active Compound. This enemy takes 25% less damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ccf5cbbbea17fa136d85ea',
    title: 'Catalyst Compound',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Active Compound. This enemy deals 30% more damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ccf606bbea17fa136d85ec',
    title: 'Sedative Compound',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Active Compound. The first player card each turn gains <b>Delay (1)</b>.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69caf81b816db67213cc7251',
    title: 'Blackhand Sigil',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Apply Mark. <p>Marked is removed only if the player uses Focus as their only turn action.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cac01eb67bdde39f741f51',
    title: 'Momentum',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'The Blackhand takes 2 actions on his next turn. <p>Playing this card does not consume an action this turn.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cabccbb67bdde39f741f43',
    title: 'Execute',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 50 damage. This attack ignores Armor. <p>If the target was Marked before this turn, this attack deals double damage.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cabc50b67bdde39f741f41',
    title: 'Pressure Point',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: "On the player's next turn, Chain overload also costs HP. <p>If the target is Marked, those costs are doubled.</p>",
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cabb6bb67bdde39f741f3f',
    title: 'Shadowstep',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: "On the player's next turn, evade the first Attack card they play.",
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ca464de7d7200184ff0542',
    title: 'Corruption Surge',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Deal 30 damage. <b>Delay (1).</b><p><b>Astral Reliquary:</b> Burn 20 SP.</p><p><b>Prime Crucible:</b> Chain.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ca46cae7d7200184ff0544',
    title: 'Containment Breach',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Deal 10 damage. <b>Delay (1).</b><p><b>Prime Crucible:</b> +40 damage.</p><p><b>Pulse Engine:</b> +30 Armor.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ca4765e7d7200184ff0546',
    title: 'Protection Protocol',
    subtitle: 'Enemy Defense',
    cardType: 'magic',
    description: 'Gain 30 Armor. <b>Delay (1).</b><p><b>Synaptic Retort:</b> Gain 30 HP.</p><p><b>Pulse Engine:</b> +50% Reflect.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69ca4812e7d7200184ff0549',
    title: 'Ruptured Ascension',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Gain 20 HP. <b>Delay (1).</b><p><b>Synaptic Retort:</b> Gain 40 HP.</p><p><b>Astral Reliquary:</b> Burn 20 SP.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cba866892c704163c0e8d1',
    title: 'Steam Release',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 20 damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cba8cf892c704163c0e8d3',
    title: 'Energy Field',
    subtitle: 'Enemy Defense',
    cardType: 'physical',
    description: '<b>Reflect 75%</b> of the next damage you would take (includes self-damage).',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cba93e892c704163c0e8d9',
    title: 'Overheat',
    subtitle: 'Enemy Utility',
    cardType: 'physical',
    description: 'Receive 30 damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cba980892c704163c0e8db',
    title: 'Steam Release',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 20 damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cba9e2892c704163c0e8dd',
    title: 'Self-repair',
    subtitle: 'Enemy Utility',
    cardType: 'physical',
    description: 'Gain 40 HP.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cbaa62892c704163c0e8e2',
    title: 'Energy Beam',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 30 damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cba76e892c704163c0e8c9',
    title: 'Steam Release',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 20 damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cba727892c704163c0e8c7',
    title: 'Astral Beam',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Burn 15 SP.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cba7ff892c704163c0e8ce',
    title: 'Crystal Shards',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Deal 60 damage. <b>Delay (2)</b>.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cd6d06da15bce3f890b819',
    title: 'Take Aim',
    subtitle: 'Enemy Utility',
    cardType: 'physical',
    description: 'Gain <b>Precision</b>.',
    keywords: ['Precision'],
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cd6d62da15bce3f890b81b',
    title: 'Deadeye Shot',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 18 damage. Ignores Reflect.<p>If <b>Precision</b>, deal 36 instead, then remove <b>Precision</b>.</p>',
    keywords: ['Precision'],
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cd6d99da15bce3f890b81d',
    title: 'Feint',
    subtitle: 'Enemy Utility',
    cardType: 'physical',
    description: 'Gain <b>Ambush</b>.',
    keywords: ['Ambush'],
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cd6e01da15bce3f890b81f',
    title: 'Gutting Strike',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 16 damage. Ignores Armor.<p>If <b>Ambush</b>, deal 34 instead, then remove <b>Ambush</b>.</p>',
    keywords: ['Ambush'],
    hpCost: 0,
    spCost: 0,
  },
  {
    id: '69cd6e52da15bce3f890b821',
    title: 'Smoke Bomb',
    subtitle: 'Enemy Defense',
    cardType: 'physical',
    description: 'Gain 25 Armor. Gain <b>Ambush</b>.',
    keywords: ['Ambush'],
    hpCost: 0,
    spCost: 0,
  },

  {
    id: 'builtin-lampkeeper-lower-the-hood',
    title: 'Lower the Hood',
    subtitle: 'Enemy Defense',
    cardType: 'magic',
    description: 'Lower the Room Light by 1 tier. Gain 18 Armor.<p>If the Room Light is Dark after this, gain 10 more Armor.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-lampkeeper-open-the-shutters',
    title: 'Open the Shutters',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Raise the Room Light by 1 tier. Burn 12 SP.<p>If the Room Light is Bright after this, deal 8 damage.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-lampkeeper-lantern-staff',
    title: 'Lantern Staff',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 16 damage.<p>If the Room Light is Dark, gain 10 Armor. If the Room Light is Bright, burn 10 SP.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-lampkeeper-trim-the-wick',
    title: 'Trim the Wick',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'If the Room Light is Steady, heal 14 HP and gain 12 Armor.<p>Otherwise, set the Room Light to Steady.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-lampkeeper-soot-burst',
    title: 'Soot Burst',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Burn 8 SP. Lower the Room Light by 1 tier.<p>If the Room Light is Dark after this, deal 14 damage.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-lampkeeper-glass-flare',
    title: 'Glass Flare',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Raise the Room Light by 1 tier.<p>If the Room Light is Bright after this, deal 20 damage and burn 12 SP.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-lampkeeper-keepers-circuit',
    title: 'Keeper’s Circuit',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'If the Room Light is Dark, gain 20 Armor.<p>If the Room Light is Steady, heal 10 HP.</p><p>If the Room Light is Bright, burn 15 SP.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-threshold-verifier-threshold-check',
    title: 'Threshold Check',
    subtitle: 'Enemy Attack',
    cardType: 'physical',
    description: 'Deal 10 damage.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-threshold-verifier-punitive-measure',
    title: 'Punitive Measure',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: 'Apply Punitive Measure for 1 turn.<p>While under Punitive Measure, lose 5 HP whenever you play a Black card.</p>',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-threshold-verifier-unauthorized-passage',
    title: 'Unauthorized Passage',
    subtitle: 'Enemy Utility',
    cardType: 'magic',
    description: "Apply Unauthorized Passage to a random card in the player's hand for 2 turns.<p>Cards under Unauthorized Passage cannot be played.</p>",
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-threshold-verifier-counterweight',
    title: 'Counterweight',
    subtitle: 'Enemy Defense',
    cardType: 'physical',
    description: 'Gain 8 Armor for each card the player played last turn.',
    hpCost: 0,
    spCost: 0,
  },
  {
    id: 'builtin-threshold-verifier-deferred-sanction',
    title: 'Deferred Sanction',
    subtitle: 'Enemy Attack',
    cardType: 'magic',
    description: 'Deal 30 damage. <b>Delay (1)</b>.<p>Deal 60 damage. <b>Delay (2)</b>.</p>',
    hpCost: 0,
    spCost: 0,
  },


{
  id: 'builtin-custodian-forfeiture',
  title: 'Forfeiture',
  subtitle: 'Enemy Utility',
  cardType: 'magic',
  description: 'For the next 3 turns, every <b>discarded</b> card due to a card effect, discard cost, or forced discard is <b>exiled</b> instead.',
  hpCost: 0,
  spCost: 0,
},
{
  id: 'builtin-custodian-inventory-tax',
  title: 'Inventory Tax',
  subtitle: 'Enemy Utility',
  cardType: 'magic',
  description: 'The player chooses one:<p>Burn 25 SP or discard a card.</p>',
  hpCost: 0,
  spCost: 0,
},
{
  id: 'builtin-custodian-reissued-violence',
  title: 'Reissued Violence',
  subtitle: 'Enemy Attack',
  cardType: 'physical',
  description: 'Deal 9 damage for each <b>exiled Attack</b>.',
  hpCost: 0,
  spCost: 0,
},
{
  id: 'builtin-custodian-salvaged-ward',
  title: 'Salvaged Ward',
  subtitle: 'Enemy Defense',
  cardType: 'physical',
  description: 'Gain 6 Armor for each <b>exiled Defense</b>.',
  hpCost: 0,
  spCost: 0,
},
{
  id: 'builtin-custodian-reprocessed-essence',
  title: 'Reprocessed Essence',
  subtitle: 'Enemy Utility',
  cardType: 'magic',
  description: 'Heal 3 HP for each <b>exiled Utility</b>.',
  hpCost: 0,
  spCost: 0,
},
{
  id: 'builtin-custodian-full-processing',
  title: 'Full Processing',
  subtitle: 'Enemy Utility',
  cardType: 'magic',
  description: 'Deal 40 damage. Gain 30 Armor. Gain 20 HP.<p>Return all exiled cards to their decks and reshuffle.</p>',
  hpCost: 0,
  spCost: 0,
},

  {
    id: 'builtin-guilt-affliction',
    title: 'Guilt',
    subtitle: 'Affliction',
    cardType: 'affliction',
    deckKind: 'magic',
    specialType: 'affliction',
    acquisitionLocked: true,
    description: 'Lose 5 HP.',
    hpCost: 0,
    spCost: 15,
  },
];

function normCardTitle(v: unknown): string {
  return String(v ?? '').trim().toLowerCase();
}

function mergeBuiltinCards(cards: RawCard[]): RawCard[] {
  const out = [...cards];
  const indexById = new Map<string, number>();
  const indexByTitle = new Map<string, number>();

  const reindexAt = (card: RawCard, idx: number) => {
    const rawId = String(card.id ?? card._id ?? '').trim().toLowerCase();
    const rawTitle = normCardTitle(card.title ?? card.name);
    if (rawId) indexById.set(rawId, idx);
    if (rawTitle) indexByTitle.set(rawTitle, idx);
  };

  for (let i = 0; i < out.length; i++) {
    reindexAt(out[i], i);
  }

  for (const builtin of BUILTIN_CARD_SPECS) {
    const idKey = String(builtin.id ?? builtin._id ?? '').trim().toLowerCase();
    const titleKey = normCardTitle(builtin.title ?? builtin.name);
    const idx = (idKey && indexById.has(idKey))
      ? indexById.get(idKey)
      : indexByTitle.get(titleKey);

    if (idx == null) {
      out.push(builtin);
      reindexAt(builtin, out.length - 1);
      continue;
    }

    const existing = out[idx];
    out[idx] = {
      ...builtin,
      ...existing,
      id: existing.id ?? existing._id ?? builtin.id,
      title: existing.title ?? existing.name ?? builtin.title,
      name: existing.name ?? existing.title ?? builtin.name ?? builtin.title,
      subtitle: existing.subtitle ?? builtin.subtitle,
      cardType: existing.cardType ?? builtin.cardType,
      description: builtin.description ?? existing.description,
      hpCost: existing.hpCost ?? builtin.hpCost,
      spCost: existing.spCost ?? builtin.spCost,
      keywords: existing.keywords ?? builtin.keywords,
      cardUrl: existing.cardUrl ?? builtin.cardUrl,
      imageUrl: existing.imageUrl ?? builtin.imageUrl,
      img: existing.img ?? builtin.img,
      image: existing.image ?? builtin.image,
    };
    reindexAt(out[idx], idx);
  }

  return out;
}

const CACHE_KEY = `combat_demo_v3_cards_cache_${encodeURIComponent(API)}`;

export async function fetchCardsWithCache(maxAgeMs: number = 5 * 60 * 1000): Promise<RawCard[]> {
  const cached = safeReadCache();
  const now = Date.now();
  if (cached && now - cached.savedAt < maxAgeMs) {
    return mergeBuiltinCards(cached.cards);
  }

  const res = await fetch(API, { headers: { 'accept': 'application/json' } });
  if (!res.ok) throw new Error(`Failed to load cards (${res.status})`);
  const cards = mergeBuiltinCards((await res.json()) as RawCard[]);
  safeWriteCache({ savedAt: now, cards });
  return cards;
}

function safeReadCache(): CacheShape | null {
  try {
    const s = localStorage.getItem(CACHE_KEY);
    if (!s) return null;
    const parsed = JSON.parse(s) as CacheShape;
    if (!parsed || !Array.isArray(parsed.cards) || typeof parsed.savedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function safeWriteCache(v: CacheShape): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
}
